import type {CreateExtArgs} from '@wix/document-manager-core'
import type {
    Pointer,
    SerializedCompStructure,
    CompRef,
    CompStructure,
    SerializedControllerData,
    ConnectionItem,
    ControllerConnectionItem
} from '@wix/document-services-types'
import _ from 'lodash'
import {DATA_TYPES, COMP_TYPES} from '../../../constants/constants'
import type {DataModelExtensionAPI} from '../../dataModel/dataModel'
import {updateConnectionsIfNeeded, updateNicknameContextByNewContainer} from './utils/nickname'
import {getComponentType} from '../../../utils/dalUtils'
import type {StructureExtensionAPI} from '../../structure'
import type {ComponentBeforeAddDataEvent} from '../hooks'
import type {ControllersExtensionAPI} from '../../controllers'

const getBoundControllerRefs = (
    {extensionAPI, pointers}: CreateExtArgs,
    containerRef: CompRef,
    compDefinition: SerializedCompStructure,
    isPage: boolean,
    oldToNewIdMap: Record<string, string>
): Pointer[] => {
    const {dataModel, controllers} = extensionAPI as DataModelExtensionAPI & ControllersExtensionAPI

    const scopedControllerRefs: Pointer[] = isPage ? [] : controllers.getAvailableScopedControllerRefs(containerRef)
    const compBoundControllers: SerializedControllerData[] = compDefinition?.controllers?.controllers ?? []
    const compBoundControllerRefs: Pointer[] = compBoundControllers.map((controller: SerializedControllerData) => {
        oldToNewIdMap[controller.id] ??= dataModel.generateItemIdWithPrefix('dataItem')
        return pointers.getPointer(oldToNewIdMap[controller.id], DATA_TYPES.data)
    })

    return [...compBoundControllerRefs, ...scopedControllerRefs]
}

const getValidConnectTargets = (
    createExtArgs: CreateExtArgs,
    isPage: boolean,
    compDefinition: SerializedCompStructure,
    containerRef: CompRef,
    oldToNewIdMap: Record<string, string>
) => {
    const {pointers, extensionAPI, dal} = createExtArgs
    const {components} = extensionAPI as StructureExtensionAPI
    const pageId = isPage ? compDefinition.id : pointers.structure.getPageOfComponent(containerRef).id
    const pageControllers = pointers.structure.getComponentsByTypeForPage(COMP_TYPES.CONTROLLER_TYPE, pageId)
    const boundControllers = getBoundControllerRefs(createExtArgs, containerRef, compDefinition, isPage, oldToNewIdMap)
    const widgets = _.filter(
        _.compact([containerRef, ...components.getAncestors(containerRef, true)]),
        ancestorRef => getComponentType(dal, ancestorRef) === COMP_TYPES.APP_WIDGET_TYPE
    )
    return [...pageControllers, ...boundControllers, ...widgets]
}
const isValidConnection = (
    {extensionAPI}: CreateExtArgs,
    validConnectTargets: Pointer[],
    oldToNewIdMap: Record<string, string>,
    connection: ControllerConnectionItem
) => {
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const controllerDataId = _.get(connection, ['controllerId'])
    return _(validConnectTargets)
        .map(controllerRef =>
            controllerRef.type === DATA_TYPES.data
                ? controllerRef.id
                : _.get(dataModel.components.getItem(controllerRef, DATA_TYPES.data), ['id'])
        )
        .some((dataItemId: string) => controllerDataId === dataItemId || dataItemId === oldToNewIdMap[controllerDataId])
}
const updateControllerIds = (oldToNewIdMap: any, compConnections: any) => {
    return _.map(compConnections, connection => {
        const currentControllerId = _.get(connection, ['controllerId'])
        if (oldToNewIdMap?.[currentControllerId]) {
            return _.assign({}, connection, {controllerId: oldToNewIdMap[currentControllerId]})
        }
        return connection
    })
}
const getUpdatedConnections = (
    createExtArgs: CreateExtArgs,
    validConnectTargets: Pointer[],
    componentConnections: ControllerConnectionItem[],
    oldToNewIdMap: Record<string, string>
) => {
    return _(componentConnections)
        .filter(connection => isValidConnection(createExtArgs, validConnectTargets, oldToNewIdMap, connection))
        .thru(_.partial(updateControllerIds, oldToNewIdMap))
        .value()
}
export const getUpdatedSerializedConnections = (
    createExtArgs: CreateExtArgs,
    containerRef: CompRef,
    compDefinition: SerializedCompStructure,
    componentConnections: ConnectionItem[],
    isPage: boolean,
    oldToNewIdMap: Record<string, string>
) => {
    const nonWixCodeConnections = _.reject(componentConnections, {type: 'WixCodeConnectionItem'})
    if (_.isEmpty(nonWixCodeConnections)) {
        return
    }
    const validConnectTargets = getValidConnectTargets(
        createExtArgs,
        isPage,
        compDefinition,
        containerRef,
        oldToNewIdMap
    )
    const newConnections = getUpdatedConnections(
        createExtArgs,
        validConnectTargets,
        nonWixCodeConnections as ControllerConnectionItem[],
        oldToNewIdMap
    )
    const wixCodeConnections = _.difference(componentConnections, nonWixCodeConnections)
    return wixCodeConnections.concat(newConnections)
}
export const updateSerializedConnectionsData = (
    createExtArgs: CreateExtArgs,
    componentRef: CompRef,
    containerRef: CompRef,
    compDefinition: CompStructure,
    isPage: boolean,
    oldToNewIdMap: Record<string, string>
) => {
    updateConnectionsIfNeeded(createExtArgs, componentRef, containerRef, compDefinition as SerializedCompStructure)
    updateNicknameContextByNewContainer(
        createExtArgs,
        componentRef,
        compDefinition as SerializedCompStructure,
        containerRef
    )

    const componentConnections: ConnectionItem[] = _.get(compDefinition, ['connections', 'items'])
    const updatedConnections = getUpdatedSerializedConnections(
        createExtArgs,
        containerRef,
        compDefinition as SerializedCompStructure,
        componentConnections,
        isPage,
        oldToNewIdMap
    )
    if (!_.isNil(updatedConnections)) {
        _.set(compDefinition, ['connections', 'items'], updatedConnections)
    }
}

export const updateSerializedConnectionsDataForComponents = (
    createExtArgs: CreateExtArgs,
    data: ComponentBeforeAddDataEvent
) => {
    const {compToAddPointer, containerPointer, componentDefinition, mappers, isPage} = data
    if (isPage || !componentDefinition.connections) return

    updateSerializedConnectionsData(
        createExtArgs,
        compToAddPointer as CompRef,
        containerPointer as CompRef,
        componentDefinition,
        isPage,
        mappers?.oldToNewIdMap
    )
}
